import { formatDistanceToNow } from 'date-fns';

// All email timestamps are displayed in IST (Indian Standard Time, UTC+5:30)
const IST_TZ = 'Asia/Kolkata';

// PERF FIX 2026-08-08 (Gap: severe scroll jank/freeze -- CPU profiling showed these two
// functions dominating CPU time during scroll, up to 100+ seconds of main-thread work for
// a single scroll session): `new Intl.DateTimeFormat(...)` was being constructed FRESH on
// every single call, and these functions run for EVERY email row + EVERY date-group header
// on EVERY render. `Intl.DateTimeFormat` construction is a genuinely expensive operation
// (locale data lookup) -- reusing cached formatter instances is the standard fix and is
// what browsers/Node explicitly recommend (`Intl.DateTimeFormat` objects are designed to be
// reused). Module-level singletons here, created once, reused for the lifetime of the tab.
const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
const timeFormatter = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', hour12: true });
const yearFormatter = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, year: 'numeric' });
const monthDayFormatter = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, month: 'short', day: 'numeric' });
const fullDateFormatter = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, day: '2-digit', month: '2-digit', year: 'numeric' });

/** Return a YYYY-MM-DD string in IST for day-boundary comparisons */
const istDateKey = (d: Date): string => dayKeyFormatter.format(d);

/**
 * Format email date for display in email list (IST)
 * Shows time for today, "Yesterday" for yesterday, MMM d for same year, else dd/MM/yyyy
 */
export const formatEmailDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();

  const todayKey = istDateKey(now);
  const dateKey = istDateKey(date);

  if (dateKey === todayKey) {
    return timeFormatter.format(date).replace('am', 'AM').replace('pm', 'PM');
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateKey === istDateKey(yesterday)) {
    return 'Yesterday';
  }

  const yearNow = yearFormatter.format(now);
  const yearDate = yearFormatter.format(date);
  if (yearNow === yearDate) {
    return monthDayFormatter.format(date);
  }

  return fullDateFormatter.format(date);
};

// Compact list-row timestamp format (e.g. "FRI AUG 7 11:15 PM") -- cached singleton
// formatter, same reasoning/pattern as the other formatters in this file (see the
// PERF FIX comment above): never construct Intl.DateTimeFormat inside a per-row function.
const compactRowFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: IST_TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
});

/**
 * Format email date for the compact list-row style (e.g. "FRI AUG 7 11:15 PM").
 * Used by the compact email list row design.
 */
export const formatEmailDateCompact = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return compactRowFormatter.format(date).toUpperCase().replace(',', '');
};

/**
 * Format full date for email detail view (IST)
 */
const fullDetailFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST_TZ,
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true,
});
export const formatFullDate = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return fullDetailFormatter.format(date) + ' IST';
};

/**
 * Format relative time (e.g., "2 hours ago") — stays relative, no tz needed
 */
export const formatRelativeTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * Format a date in IST with explicit format options (replaces direct date-fns format() calls)
 * Use this wherever email dates need IST display.
 */
export const formatISTDate = (dateString: string | Date, options: Intl.DateTimeFormatOptions = {}): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const defaults: Intl.DateTimeFormatOptions = {
    timeZone: IST_TZ,
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  };
  return new Intl.DateTimeFormat('en-IN', { ...defaults, ...options, timeZone: IST_TZ }).format(date);
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Format email address for display
 */
export const formatEmailAddress = (email: { name?: string; address: string } | string): string => {
  if (typeof email === 'string') {
    // Handle string email (could be "Name <email>" or just "email")
    const match = email.match(/^([^<]*)<([^>]+)>$/);
    if (match) {
      return match[1].trim() || match[2].split('@')[0];
    }
    return email.split('@')[0];
  }
  if (email.name) {
    return email.name;
  }
  return email.address.split('@')[0];
};

/**
 * Get email address from string or object
 */
export const getEmailAddressString = (email: { name?: string; address: string } | string): string => {
  if (typeof email === 'string') {
    const match = email.match(/^[^<]*<([^>]+)>$/);
    if (match) return match[1];
    return email;
  }
  return email.address;
};

/**
 * Get initials from name or email
 */
export const getInitials = (nameOrEmail: string): string => {
  const name = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  
  return name.substring(0, 2).toUpperCase();
};

/**
 * Generate a consistent color from a string
 */
export const stringToColor = (str: string): string => {
  const colors = [
    '#f44336', '#e91e63', '#9c27b0', '#673ab7',
    '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4',
    '#009688', '#4caf50', '#8bc34a', '#cddc39',
    '#ffc107', '#ff9800', '#ff5722', '#795548',
  ];
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Strip HTML tags from content
 */
export const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Get preview text from email body
 */
export const getEmailPreview = (email: { textBody?: string; htmlBody?: string; body?: string }, maxLength: number = 100): string => {
  const content = email.textBody || (email.htmlBody ? stripHtml(email.htmlBody) : email.body || '');
  return truncateText(content.replace(/\s+/g, ' ').trim(), maxLength);
};

/**
 * Parse email addresses from a string
 */
export const parseEmailAddresses = (input: string): Array<{ name?: string; address: string }> => {
  const emails: Array<{ name?: string; address: string }> = [];
  const regex = /(?:"?([^"<]*)"?\s*)?<?([^\s,<>]+@[^\s,<>]+)>?/g;
  let match;
  
  while ((match = regex.exec(input)) !== null) {
    emails.push({
      name: match[1]?.trim() || undefined,
      address: match[2].trim(),
    });
  }
  
  return emails;
};

/**
 * Validate email address
 */
export const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Format number with commas
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * Check if the user is online
 */
export const isOnline = (): boolean => {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Get file extension icon mapping
 */
export const getFileIcon = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  const iconMap: Record<string, string> = {
    pdf: 'picture_as_pdf',
    doc: 'description',
    docx: 'description',
    xls: 'table_chart',
    xlsx: 'table_chart',
    ppt: 'slideshow',
    pptx: 'slideshow',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    svg: 'image',
    zip: 'folder_zip',
    rar: 'folder_zip',
    '7z': 'folder_zip',
    mp3: 'audio_file',
    wav: 'audio_file',
    mp4: 'video_file',
    mov: 'video_file',
    avi: 'video_file',
    txt: 'article',
    csv: 'table_chart',
    json: 'code',
    js: 'code',
    ts: 'code',
    html: 'code',
    css: 'code',
  };
  
  return iconMap[ext] || 'attachment';
};
